import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable } from 'rxjs';

import {
  Client,
  DlmsAttributes,
  EventCodeMappingDto,
  EventLogLabelsDataIn,
  MeterModelDataOut,
  PermissionsEnum,
} from '../../../../../api/api-reference';
import { generateFileNameAppendix } from '../../../../../helper/download-filename';
import { AuthService } from '../../../../../services/auth.service';
import { FileService } from '../../../../../services/file.service';

@Component({
  selector: 'app-meter-model-event-codes',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    AngularSvgIconModule,
  ],
  templateUrl: './meter-model-event-codes.component.html',
  styleUrl: './meter-model-event-codes.component.scss',
})
export class MeterModelEventCodesComponent implements OnInit {
  readonly eventCodeRegisters: DlmsAttributes[] = [
    DlmsAttributes.Data_EventCode1Value,
    DlmsAttributes.Data_EventCode2Value,
    DlmsAttributes.Data_EventCode3Value,
    DlmsAttributes.Data_EventCode4Value,
    DlmsAttributes.Data_EventCode5Value,
    DlmsAttributes.Data_EventCode6Value,
  ];

  PermissionEnum = PermissionsEnum;
  meterModel: MeterModelDataOut = new MeterModelDataOut();
  meterModelId = '';
  activeRegister: DlmsAttributes = DlmsAttributes.Data_EventCode1Value;

  mappings: EventCodeMappingDto[] = [];
  // Ids of the mappings the user deleted, kept here until Save sends them in one call.
  pendingDeletions = new Set<string>();
  // Custom event log names, one per tab; an empty entry shows the default name.
  labels: string[] = [];
  savedLabels: string[] = [];

  constructor(
    private client: Client,
    private toastr: ToastrService,
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private fileService: FileService,
    public authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.meterModel.model = history.state['model'];
    this.meterModel.firmwareVersion = history.state['firmwareVersion'];

    this.route.params.subscribe(params => {
      this.meterModelId = params['id'];
      this.loadMappings();
      this.loadLabels();
    });
  }

  download(): void {
    const fileNameApendix = generateFileNameAppendix();

    this.client.downloadEventCodeMappings(this.meterModelId).subscribe({
      next: response => {
        this.fileService.downloadFile(
          response.data,
          response.fileName || `EventCodeMapping${fileNameApendix}`,
        );
      },
      error: err => this.toastr.error(err),
    });
  }

  navigate(route: string): void {
    this.router.navigate([route]);
  }

  goBack(): void {
    this.location.back();
  }

  getRegisterNumber(register: DlmsAttributes): number {
    return this.eventCodeRegisters.indexOf(register) + 1;
  }

  getMappings(register: DlmsAttributes): EventCodeMappingDto[] {
    return this.mappings.filter(
      mapping => mapping.attribute === register && !this.pendingDeletions.has(mapping.id),
    );
  }

  get hasPendingDeletions(): boolean {
    return this.pendingDeletions.size > 0;
  }

  get hasPendingLabels(): boolean {
    return this.labels.some((label, i) => (label || '').trim() !== (this.savedLabels[i] || ''));
  }

  get hasPendingChanges(): boolean {
    return this.hasPendingDeletions || this.hasPendingLabels;
  }

  deleteMapping(mapping: EventCodeMappingDto): void {
    this.pendingDeletions.add(mapping.id);
  }

  save(): void {
    const requests: Observable<string>[] = [];
    if (this.hasPendingLabels) {
      // The server expects one entry per log, also when the labels never loaded.
      const labels = this.eventCodeRegisters.map((_, i) => this.labels[i] || '');
      requests.push(this.client.saveEventLogLabels(new EventLogLabelsDataIn({ meterModelId: this.meterModelId, labels })));
    }
    if (this.hasPendingDeletions) requests.push(this.client.deleteEventCodeMappings([...this.pendingDeletions]));
    if (requests.length === 0) return;

    forkJoin(requests).subscribe({
      next: responses => {
        this.toastr.success(responses[0]);
        this.pendingDeletions.clear();
        this.loadMappings();
        this.loadLabels();
      },
      error: err => this.toastr.error(err),
    });
  }

  private loadLabels(): void {
    this.client.getEventLogLabels(this.meterModelId).subscribe({
      next: labels => {
        this.labels = [...labels];
        this.savedLabels = [...labels];
      },
      error: err => this.toastr.error(err),
    });
  }

  private loadMappings(): void {
    this.client.getEventCodeMappings(this.meterModelId).subscribe({
      next: mappings => (this.mappings = mappings),
      error: err => this.toastr.error(err),
    });
  }
}
